import { useEffect, useRef } from "react";
import { toast } from "@/components/ui/sonner";

/**
 * Ensures users on the published site are prompted to refresh when a new build is available.
 * This prevents running stale cached JS (common on iOS/PWA).
 * 
 * The PWA registration is lazy-loaded to avoid blocking the critical render path.
 */
export function PWAUpdatePrompt() {
  const hasPromptedRef = useRef(false);

  useEffect(() => {
    // Defer PWA registration until AFTER page load to break the network dependency chain
    // This ensures workbox-window is not fetched during the critical path
    const registerPWA = async () => {
      try {
        const { registerSW } = await import("virtual:pwa-register");
        
        registerSW({
          onNeedRefresh() {
            // Avoid duplicate prompts if multiple SW events fire.
            if (hasPromptedRef.current) return;
            hasPromptedRef.current = true;

            toast("Update available", {
              description: "Refresh to get the latest fixes.",
              duration: Infinity,
              action: {
                label: "Refresh",
                onClick: () => window.location.reload(),
              },
            });
          },
        });
      } catch (error) {
        // PWA registration failed silently - not critical for app functionality
        console.debug("PWA registration skipped:", error);
      }
    };

    // Wait for page load, then defer further with requestIdleCallback
    // This breaks the critical chain: HTML → main.js → workbox-window
    const deferRegistration = () => {
      if ("requestIdleCallback" in window) {
        (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => registerPWA());
      } else {
        setTimeout(() => registerPWA(), 100);
      }
    };

    if (document.readyState === "complete") {
      deferRegistration();
    } else {
      window.addEventListener("load", deferRegistration, { once: true });
    }

    return () => {
      window.removeEventListener("load", deferRegistration);
    };
  }, []);

  // Reset the prompt flag after a while (in case the user ignores it for a long time).
  useEffect(() => {
    const t = window.setTimeout(() => {
      hasPromptedRef.current = false;
    }, 60_000);
    return () => window.clearTimeout(t);
  }, []);

  return null;
}
